import React, { useEffect, useState } from 'react'
import { portal } from '../portalApi.js'
import { fmt, fmtFixed } from '../api.js'
import NoteDrawer from '../components/NoteDrawer.jsx'
import LineExcelImport from '../components/LineExcelImport.jsx'
import RichTextEditor from '../components/RichTextEditor.jsx'
import AttachmentPicker, { MAX_ATTACHMENT_BYTES } from '../components/AttachmentPicker.jsx'
import CardPicker from '../components/CardPicker.jsx'
import { CURRENCIES, EMPTY_LINE, fieldsFor, isFxForm } from '../components/lineFields.js'

const SUB_SCREEN_KEY = {
  inbox: 'edoc_inbox', draft: 'edoc_draft', mine: 'edoc_mine',
  approver: 'edoc_approver', transfers: 'edoc_transfers',
}

// 전자결재 — 지출결의 기안 · 결재함 · 결재현황 · 자금이체 관리 (/api/edoc/*)
const FORMS = [
  { type: '지출결의서_계좌이체', label: '지출결의서 · 원화', desc: '거래처 계좌로 이체하는 지출' },
  { type: '지출결의서_외화송금', label: '지출결의서 · 외화', desc: 'SWIFT · 통화 · 수취계좌가 함께 들어간다' },
  { type: '지출결의서_법인카드', label: '지출결의서 · 법인카드', desc: '들어와 있는 카드 사용내역에서 골라 기안한다' },
  { type: '품의서', label: '품의서', desc: '구매 · 계약 · 기타 — 지출 내역 없이 상세 내용만' },
]
const PROPOSAL = '품의서'
const CARD_FORM = '지출결의서_법인카드'
const FORM_TYPES = FORMS.map((f) => f.type)
const formLabel = (t) => (FORMS.find((f) => f.type === t)?.label ?? t)
const INPUT_MODES = ['직접 입력', '엑셀 업로드']

const fmtSize = (n) => (n >= 1024 * 1024 ? `${(n / 1024 / 1024).toFixed(1)}MB` : `${Math.max(1, Math.round(n / 1024))}KB`)

// 외화는 소수 두 자리까지 쓴다 (원화는 정수)
const amtDigits = (formType) => (isFxForm(formType) ? 2 : 0)

// 원문 링크는 바깥(WORK)이 보내온 값이다. javascript: 같은 것이 섞여 들어오면
// 누르는 순간 우리 화면에서 남의 코드가 돈다 — http(s) 만 통과시킨다.
const 원문링크 = (v) => {
  const s = String(v || '').trim()
  return /^https?:\/\//i.test(s) ? s : ''
}
const fmtAmt = (v, digits) => (digits ? fmtFixed(v, digits) : fmt(v))

// 통화가 섞이면 합계 하나로는 뜻이 없어 통화별로도 보여준다
function byCurrency(lines) {
  const map = new Map()
  for (const l of lines) {
    const cur = l.currency || CURRENCIES[0]
    map.set(cur, (map.get(cur) || 0) + (Number(l.amount) || 0))
  }
  return [...map.entries()]
}

// 은행 표기 통일 — '신한은행'·'주식회사 신한' 처럼 써도 표준명('신한')으로 맞춘다.
// bank.py의 norm_bank와 같은 규칙 (키워드 표는 /api/edoc/banks로 받아온다)
const bankKey = (s) => String(s || '').toLowerCase().replace(/\s|은행|주식회사|\(주\)/g, '')
function matchBank(raw, banks) {
  const s = bankKey(raw)
  if (!s) return ''
  const hit = banks.find((b) => b.keys.some((k) => s.includes(k)))
  return hit ? hit.name : String(raw).trim()
}

// 결재선 — 결재와 합의를 줄을 나눠 보여준다
function ApprovalLine({ steps }) {
  const of = (g) => steps.filter((s) => (s.grp ?? s.group ?? '결재') === g)
  const rows = [['결재', of('결재')], ['합의', of('합의')]].filter(([, list]) => list.length)
  if (!rows.length) return <span className="muted">결재선이 없습니다</span>
  return (
    <div className="appr-lines">
      {rows.map(([label, list]) => (
        <div key={label} className="appr-line">
          <span className="appr-label">{label}</span>
          <div className="row" style={{ gap: 6, flexWrap: 'wrap' }}>
            {list.map((s, i) => (
              <span key={s.id ?? i} className={`step-chip ${s.status === '승인' ? 'done' : s.status === '반려' ? 'rejected' : ''}`}>
                {s.approver_name ?? s.name}
                {s.status && s.status !== '대기' ? ` ${s.status === '승인' ? '✓' : '✗'}` : ''}
              </span>
            ))}
          </div>
        </div>
      ))}
    </div>
  )
}

function DocDetail({ id, onActed }) {
  const [data, setData] = useState(null)
  const [err, setErr] = useState('')
  const [comment, setComment] = useState('')

  const load = () => portal.get(`/api/edoc/docs/${id}`).then(setData).catch((e) => setErr(e.message))
  useEffect(() => { load() }, [id])

  const act = async (decision) => {
    try {
      await portal.post(`/api/edoc/steps/${data.myPendingStepIds[0]}/act`, { decision, comment: comment || '' })
      onActed(`${decision} 완료`)
    } catch (e) { setErr(e.message) }
  }

  if (err) return <div className="error-box">{err}</div>
  if (!data) return <div className="muted">불러오는 중…</div>
  const { doc, lines, steps, attachments = [] } = data
  const docFields = fieldsFor(doc.form_type)

  return (
    <div className="detail-box">
      <div className="row" style={{ marginBottom: 6 }}>
        <b>{doc.doc_num}</b><span>{doc.title}</span>
        <span className="muted">{doc.form_type} · 기안 {doc.drafter_dept} · 지급일 {doc.pay_date || '-'}</span>
        <span className={doc.status === '완료' ? 'ok' : doc.status === '반려' ? 'warn' : ''}>{doc.status}</span>
      </div>
      {/* WORK 에서 넘어온 문서는 여기서 원문으로 돌아갈 수 있어야 한다 — 우리 쪽은
          받아 적은 사본이라, 첨부나 댓글은 저쪽에만 있는 경우가 있다.
          열 이름(notion_link)은 예전 문서가 쓰던 그대로라 이름만 출처에 맞춘다. */}
      {원문링크(doc.notion_link) && (
        <div className="row" style={{ marginBottom: 6, gap: 6 }}>
          {/* appr-label 은 «결재»·«참조» 두 글자에 맞춘 42px 고정폭이라 여기 쓰면 글자가 겹친다 */}
          <span className="muted" style={{ flex: 'none', fontSize: 12, fontWeight: 700 }}>
            {doc.ext_source ? 'WORK 원문' : '노션링크'}
          </span>
          <a href={원문링크(doc.notion_link)} target="_blank" rel="noopener noreferrer"
             style={{ overflowWrap: 'anywhere' }}>{doc.notion_link}</a>
        </div>
      )}
      <ApprovalLine steps={steps} />
      <table style={{ marginTop: 10 }}>
        <thead>
          <tr>
            {docFields.map((f) => <th key={f.key} className={f.key === 'amount' ? '' : 'l'}>{f.label}</th>)}
            <th>이체</th>
          </tr>
        </thead>
        <tbody>
          {lines.map((l) => (
            <tr key={l.id}>
              {docFields.map((f) => (
                <td key={f.key} className={f.key === 'amount' ? '' : 'l'}>
                  {f.key === 'amount' ? fmtAmt(l.amount, amtDigits(doc.form_type)) : l[f.key]}
                </td>
              ))}
              {/* 기안자·결재자도 언제 나갔는지 알 수 있게 이체일까지 보여준다 */}
              <td className="c">
                {l.transfer_status === '이체완료'
                  ? <span className="ok">✓ {l.transfer_date || ''}</span>
                  : doc.status === '완료' ? <span className="muted">미이체</span> : ''}
              </td>
            </tr>
          ))}
          <tr className="total">
            {/* 문서 헤더의 total_amount는 정수라 외화 소수점이 깎인다 — 라인 합으로 보여준다 */}
            <td className="l" colSpan={docFields.length - 1}>합계</td>
            <td>{fmtAmt(lines.reduce((s, l) => s + (Number(l.amount) || 0), 0), amtDigits(doc.form_type))}</td>
            <td />
          </tr>
        </tbody>
      </table>
      {(() => {
        let r = []
        try { r = JSON.parse(doc.refs_json || '[]') } catch { r = [] }
        return r.length ? (
          <div className="row" style={{ marginTop: 10, gap: 6, flexWrap: 'wrap' }}>
            <span className="appr-label">참조</span>
            {r.map((x) => <span key={x} className="step-chip">{x}</span>)}
          </div>
        ) : null
      })()}
      {doc.note && (
        <div style={{ marginTop: 12 }}>
          <div className="muted" style={{ fontSize: 12, marginBottom: 4 }}>상세 내용</div>
          {/* 저장 시 서버(clean_rich_html)에서 허용 태그만 남긴 HTML */}
          <div className="doc-body" dangerouslySetInnerHTML={{ __html: doc.note }} />
        </div>
      )}
      {attachments.length > 0 && (
        <div style={{ marginTop: 12 }}>
          <div className="muted" style={{ fontSize: 12, marginBottom: 4 }}>증빙 첨부</div>
          <div className="attach-list">
            {attachments.map((a) => (
              <div key={a.id} className="attach-item">
                <span className="name">{a.filename}</span>
                <span className="muted">{fmtSize(a.size)}</span>
                <a className="btn secondary" href={`/api/edoc/docs/${doc.id}/attachments/${a.id}`}>내려받기</a>
              </div>
            ))}
          </div>
        </div>
      )}
      {data.myPendingStepIds.length > 0 && (
        <div className="row" style={{ marginTop: 10 }}>
          <input placeholder="코멘트(선택)" style={{ width: 240 }} value={comment} onChange={(e) => setComment(e.target.value)} />
          <button className="btn" onClick={() => act('승인')}>승인</button>
          <button className="btn danger" onClick={() => act('반려')}>반려</button>
        </div>
      )}
    </div>
  )
}

function Inbox({ onToast }) {
  const [list, setList] = useState(null)
  const [openId, setOpenId] = useState(null)
  const [err, setErr] = useState('')
  const load = () => portal.get('/api/edoc/docs?box=inbox').then(setList).catch((e) => setErr(e.message))
  useEffect(() => { load() }, [])
  if (err) return <div className="error-box">{err}</div>
  if (!list) return <div className="muted">불러오는 중…</div>
  return (
    <div className="panel">
      <table>
        <thead><tr><th>문서번호</th><th className="l">제목</th><th className="l">기안자</th><th>금액</th><th>기안일</th><th>차례</th><th /></tr></thead>
        <tbody>
          {list.length === 0 && <tr><td colSpan={7} className="c muted">결재할 문서가 없습니다</td></tr>}
          {list.map(({ doc, step, eligible, activeNames }) => (
            <React.Fragment key={`${doc.id}-${step.id}`}>
              <tr>
                <td className="c">{doc.doc_num}</td>
                <td className="l">{doc.title}</td>
                <td className="l">{doc.drafter_name} · {doc.drafter_dept}</td>
                <td>{fmt(doc.total_amount)}</td>
                <td className="c">{String(doc.created_at || '').slice(0, 10)}</td>
                <td className="c">{eligible ? <span className="ok">지금 내 차례</span> : <span className="muted">대기 ({(activeNames || []).join(', ')})</span>}</td>
                <td className="c"><button className="btn secondary" onClick={() => setOpenId(openId === doc.id ? null : doc.id)}>{openId === doc.id ? '닫기' : '열기'}</button></td>
              </tr>
              {openId === doc.id && (
                <tr><td colSpan={7} style={{ background: '#F8FAFC' }}>
                  <DocDetail id={doc.id} onActed={(m) => { onToast(m); setOpenId(null); load() }} />
                </td></tr>
              )}
            </React.Fragment>
          ))}
        </tbody>
      </table>
    </div>
  )
}

// 어떤 양식으로 기안할지 먼저 묻는다. 양식에 따라 표의 열과 결재선이 달라지므로,
// 제목부터 치게 해 놓고 나중에 양식을 바꾸면 앞서 적은 것이 어긋난다.
// 결재선 편집기. 양식·금액으로 기본값이 정해지지만 케이스마다 편차가 있어 고칠 수 있어야
// 한다. 작성자는 뺄 수 없다 - 기안한 사람이 빠지면 문서가 누구 것인지 알 수 없다.
// 결재는 순서가 뜻을 가지므로(위에서 아래로 올라간다) 자리를 옮길 수 있게 하고,
// 합의는 순서가 상관없어 넣고 빼기만 한다.
const 그룹 = (s) => s.grp ?? s.group ?? '결재'
const 이름 = (s) => s.approver_name ?? s.name ?? ''

function ApprovalLineEditor({ steps, people, onChange, refs, onRefsChange }) {
  const 소속 = (p) => (p.exec_role ? '임원' : (p.team || p.division || '기타'))
  const 부서들 = []
  for (const p of people) if (!부서들.includes(소속(p))) 부서들.push(소속(p))
  // 서버가 조직도 순서로 내려 주므로 이어지는 것끼리 묶으면 그대로 본부별 무리가 된다
  const 무리 = []
  for (const p of people) {
    const g = p.exec_role ? '임원' : (p.division || '기타')
    if (!무리.length || 무리[무리.length - 1][0] !== g) 무리.push([g, []])
    무리[무리.length - 1][1].push(p)
  }

  const 결재 = steps.filter((s) => 그룹(s) === '결재')
  const 합의 = steps.filter((s) => 그룹(s) === '합의')
  const 적용 = (a, b) => onChange([
    ...a.map((s, i) => ({ ...s, group: '결재', grp: '결재', seq: i })),
    ...b.map((s, i) => ({ ...s, group: '합의', grp: '합의', seq: i })),
  ])
  const 빼기 = (g, i) => {
    if (g === '참조') return onRefsChange(refs.filter((_, j) => j !== i))
    return g === '결재' ? 적용(결재.filter((_, j) => j !== i), 합의)
                        : 적용(결재, 합의.filter((_, j) => j !== i))
  }
  const 옮기기 = (i, d) => {
    const n = [...결재]
    if (i + d < 1 || i + d >= n.length) return      // 0번은 작성자 자리라 비켜 두지 않는다
    ;[n[i], n[i + d]] = [n[i + d], n[i]]
    적용(n, 합의)
  }
  const 넣기 = (g, name) => {
    if (!name) return
    if (g === '참조') return refs.includes(name) ? undefined : onRefsChange([...refs, name])
    const 사람 = people.find((p) => p.name === name)
    const 새것 = { group: g, grp: g, name, role: 사람?.position || g, id: 사람?.id ?? null }
    return g === '결재' ? 적용([...결재, 새것], 합의) : 적용(결재, [...합의, 새것])
  }

  // 사람 하나를 고르는 자리. 결재·합의·참조 모두 여기서는 한 명씩만 붙는다.
  const 사람고르개 = (g) => (
    <select className="appr-add" value="" onChange={(e) => 넣기(g, e.target.value)}>
      <option value="">＋ {g}자 추가</option>
      {무리.map(([본부, 사람들]) => (
        <optgroup key={본부} label={본부}>
          {사람들.map((p) => (
            <option key={p.id} value={p.name}>
              {p.name} {p.position || ''}{p.team && p.team !== 본부 ? ` · ${p.team}` : ''}
            </option>
          ))}
        </optgroup>
      ))}
    </select>
  )

  // 부서를 통째로 거는 것은 참조뿐이다. 결재나 합의에 부서를 걸면 그 부서 아무나 한 명이
  // 눌러 끝낼 수 있게 되어 결재가 결재가 아니게 된다. 참조는 열람이라 그래도 된다.
  const 부서고르개 = () => (
    <select className="appr-add" value="" onChange={(e) => 넣기('참조', e.target.value)}>
      <option value="">＋ 부서 전체 추가</option>
      {부서들.filter((d) => !refs.includes(d)).map((d) => <option key={d} value={d}>{d}</option>)}
    </select>
  )

  const 줄 = (라벨, 목록) => (
    <div className="appr-line" key={라벨}>
      <span className="appr-label">{라벨}</span>
      <div className="row" style={{ gap: 6, flexWrap: 'wrap' }}>
        {목록.map((s, i) => {
          const nm = 라벨 === '참조' ? s : 이름(s)
          const 부서칩 = 라벨 === '참조' && !people.some((p) => p.name === nm)
          return (
            <span key={`${라벨}${i}`}
              className={`step-chip${nm === '(미지정)' ? ' rejected' : ''}${부서칩 ? ' dept' : ''}`}>
              {라벨 === '결재' && i > 0 && (
                <>
                  <button className="chip-x" title="앞으로" onClick={() => 옮기기(i, -1)}>‹</button>
                  <button className="chip-x" title="뒤로" onClick={() => 옮기기(i, 1)}>›</button>
                </>
              )}
              {nm}
              {라벨 !== '참조' && s.role && s.role !== 라벨
                ? <span className="chip-role"> {s.role}</span> : null}
              {!(라벨 === '결재' && i === 0) && (
                <button className="chip-x" title="빼기" onClick={() => 빼기(라벨, i)}>×</button>
              )}
            </span>
          )
        })}
        {사람고르개(라벨)}
        {라벨 === '참조' && 부서고르개()}
      </div>
    </div>
  )
  return (
    <div className="appr-lines">
      {줄('결재', 결재)}
      {줄('합의', 합의)}
      {줄('참조', refs)}
    </div>
  )
}

function FormPicker({ current, onSelect, onClose }) {
  useEffect(() => {
    const esc = (e) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', esc)
    return () => window.removeEventListener('keydown', esc)
  }, [onClose])
  return (
    <div className="modal-backdrop" onMouseDown={onClose}>
      <div className="modal-panel form-picker" onMouseDown={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>결재 양식 선택</h3>
          <button className="modal-close" onClick={onClose} aria-label="닫기">×</button>
        </div>
        <div className="form-picker-list">
          {FORMS.map((f) => (
            <button key={f.type} type="button"
              className={`form-card${f.type === current ? ' on' : ''}`}
              onClick={() => onSelect(f.type)}>
              <span className="ft">{f.label}</span>
              <span className="fd">{f.desc}</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  )
}

function Draft({ onToast, onDone }) {
  // 양식을 고르기 전에는 비워 둔다. 그래야 팝업이 먼저 뜬다.
  const [form, setForm] = useState({ form_type: '', title: '', pay_date: '', note: '', refs: [] })
  const [picking, setPicking] = useState(true)
  const [lines, setLines] = useState([{ ...EMPTY_LINE }])
  const [preview, setPreview] = useState([])
  const [apprLine, setApprLine] = useState(null)  // 사람이 고친 결재선. null 이면 기본값을 따른다.
  const [people, setPeople] = useState([])
  const [cats, setCats] = useState([])
  const [banks, setBanks] = useState([])
  const [err, setErr] = useState('')
  const [mode, setMode] = useState('직접 입력')
  const [files, setFiles] = useState([])
  const [busy, setBusy] = useState(false)
  const total = lines.reduce((s, l) => s + (Number(l.amount) || 0), 0)
  const oversized = files.some((f) => f.size > MAX_ATTACHMENT_BYTES)
  const proposal = form.form_type === PROPOSAL
  const card = form.form_type === CARD_FORM
  const hasDetail = !!String(form.note || '').replace(/<[^>]*>|&nbsp;|\s/g, '')
  const apprSteps = apprLine ?? preview
  const fx = isFxForm(form.form_type)
  const fields = fieldsFor(form.form_type)
  const currencies = fx ? byCurrency(lines.filter((l) => l.vendor || l.amount)) : []

  // 양식을 바꾸면 표의 열이 달라진다 — 외화로 가면 통화를 채우고, 원화로 오면 외화 전용 칸을 비운다
  const changeForm = (form_type) => {
    if ((form_type === CARD_FORM) !== (form.form_type === CARD_FORM)) {
      setLines([{ ...EMPTY_LINE }])
    }
    setForm((f) => ({ ...f, form_type }))
    setLines((p) => p.map((l) => (isFxForm(form_type)
      ? { ...l, currency: l.currency || CURRENCIES[0] }
      : { ...l, currency: '', swift: '' })))
    setPicking(false)
    setApprLine(null)
  }

  useEffect(() => {
    if (!form.form_type) { setPreview([]); return undefined }
    const t = setTimeout(() => {
      portal.get(`/api/edoc/appr-preview?form=${encodeURIComponent(form.form_type)}&total=${total}`)
        .then(setPreview).catch(() => setPreview([]))
    }, 300)
    return () => clearTimeout(t)
  }, [total, form.form_type])

  // 기초설정 > 계정과목에서 관리자가 열어둔(관리) 계정과목 목록 그대로 재사용
  useEffect(() => {
    portal.get('/api/fa/meta/categories').then(setCats).catch(() => setCats([]))
    portal.get('/api/edoc/banks').then(setBanks).catch(() => setBanks([]))
    portal.get('/api/edoc/people').then(setPeople).catch(() => setPeople([]))
  }, [])

  const setLine = (i, patch) => setLines((p) => p.map((l, j) => (j === i ? { ...l, ...patch } : l)))

  // 엑셀 업로드 결과 반영 — '교체'는 통째로, '추가'는 비어있는 기존 행은 버리고 이어붙인다.
  // 은행은 '신한은행' 같은 표기로 들어오므로 드롭다운 값(표준명)으로 맞춰서 넣는다.
  const applyExcel = (rows, mode) => {
    const imported = rows.map((r) => ({
      ...EMPTY_LINE, ...r,
      bank: fx ? r.bank : matchBank(r.bank, banks),
    }))
    setLines((p) => (mode === 'replace'
      ? imported
      : [...p.filter((l) => l.vendor || l.amount), ...imported]))
  }

  const submit = async () => {
    setErr('')
    setBusy(true)
    try {
      const payload = {
        ...form,
        appr_line: apprSteps.filter((s) => s.role !== '작성자')
          .map((s) => ({ group: 그룹(s), name: 이름(s) })),
        lines: lines.filter((l) => l.vendor && l.amount).map((l) => ({
          ...l,
          amount: Number(l.amount),
          swift: fx ? (l.swift || '') : '',
          currency: fx ? (l.currency || CURRENCIES[0]) : 'KRW',
        })),
      }
      // 증빙이 있으면 문서와 첨부를 한 번에 올린다 (문서만 남는 상황을 막는다)
      const r = files.length
        ? await portal.upload('/api/edoc/docs', payload, files)
        : await portal.post('/api/edoc/docs', payload)
      onToast(`기안 완료 (문서 #${r.id}${r.attachments ? ` · 증빙 ${r.attachments}건` : ''})`)
      onDone()
    } catch (e) { setErr(e.message) } finally { setBusy(false) }
  }

  if (!form.form_type) {
    return (
      <>
        <div className="panel">
          <span className="muted">작성할 결재 양식을 선택하세요.</span>{' '}
          <button className="btn" onClick={() => setPicking(true)}>양식 선택</button>
        </div>
        {picking && <FormPicker current="" onSelect={changeForm} onClose={() => setPicking(false)} />}
      </>
    )
  }

  return (
    <>
      {err && <div className="error-box">{err}</div>}
      {picking && (
        <FormPicker current={form.form_type} onSelect={changeForm} onClose={() => setPicking(false)} />
      )}
      <div className="panel">
        <div className="row">
          <span className="form-chip">{formLabel(form.form_type)}</span>
          <button className="btn secondary tiny" onClick={() => setPicking(true)}>양식 변경</button>
          <input placeholder="제목*" style={{ width: 280 }} value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
          {!proposal && <>
            <span className="muted">지급 희망일</span>
            <input type="date" value={form.pay_date} onChange={(e) => setForm({ ...form, pay_date: e.target.value })} />
          </>}
        </div>
      </div>

      <div className="sec-title">결재선 <span className="muted" style={{ fontWeight: 400, fontSize: 12 }}>
        {proposal ? '(양식별 자동)' : '(금액별 자동)'}</span>
        {apprLine && <button className="btn secondary tiny" style={{ marginLeft: 8 }}
          onClick={() => setApprLine(null)}>기본값으로</button>}</div>
      <div className="panel">
        <ApprovalLineEditor steps={apprSteps} people={people} onChange={setApprLine}
          refs={form.refs} onRefsChange={(r) => setForm((f) => ({ ...f, refs: r }))} />
        <div className="hint">
          {apprLine
            ? '기본 결재선을 고쳤습니다. 금액이 바뀌어도 이대로 나갑니다 — 되돌리려면 [기본값으로].'
            : '양식과 금액에 맞는 기본 결재선입니다. 여기서 빼거나 더하거나 순서를 바꿀 수 있습니다.'}
          <br />
          결재·합의는 한 명씩만 세웁니다. 부서를 걸면 그 부서 아무나 한 명이 눌러 끝낼 수 있어서입니다.
          참조는 결재에 관여하지 않고 열어 보기만 하므로 부서를 통째로 걸 수 있습니다.
        </div>
      </div>

      {card && (<>
      <div className="sec-title">법인카드 사용 내역</div>
      <div className="panel">
        <CardPicker cats={cats} onChange={setLines} />
      </div>
      </>)}

      {!proposal && !card && (<>
      <div className="sec-title">지출 내역</div>
      <div className="panel">
        <div className="muted" style={{ fontSize: 12, marginBottom: 8 }}>입력 방식</div>
        <div className="seg">
          {INPUT_MODES.map((m) => (
            <label key={m} className={mode === m ? 'on' : ''}>
              <input type="radio" name="edoc-input-mode" checked={mode === m} onChange={() => setMode(m)} />
              {m}
            </label>
          ))}
        </div>
        {mode === '엑셀 업로드' && (
          <div style={{ marginTop: 12 }}><LineExcelImport cats={cats} fields={fields} onApply={applyExcel} /></div>
        )}
      </div>

      <div className="panel">
      <table className="line-grid">
        <colgroup>
          {fields.map((f) => <col key={f.key} style={{ width: f.width }} />)}
          <col style={{ width: 64 }} />
        </colgroup>
        <thead>
          <tr>
            {fields.map((f) => (
              <th key={f.key} className={f.key === 'amount' ? '' : 'l'}>
                {f.label}{(f.key === 'vendor' || f.key === 'amount') ? '*' : ''}
              </th>
            ))}
            <th />
          </tr>
        </thead>
        <tbody>
          {lines.map((l, i) => (
            <tr key={i}>
              {fields.map((f) => (
                <td key={f.key}>
                  {f.key === 'bank' && !fx ? (
                    /* 은행은 표준 목록에서 고른다 (엑셀에서 들어온 비표준 표기는 아래에서 살려둔다) */
                    <select value={l.bank} onChange={(e) => setLine(i, { bank: e.target.value })}>
                      <option value="">선택</option>
                      {banks.map((b) => <option key={b.code} value={b.name}>{b.name}</option>)}
                      {l.bank && !banks.some((b) => b.name === l.bank) && <option value={l.bank}>{l.bank}</option>}
                    </select>
                  ) : f.key === 'account_cat' ? (
                    <select value={l.account_cat} onChange={(e) => setLine(i, { account_cat: e.target.value })}>
                      <option value="">선택</option>
                      {cats.map((c) => <option key={c.code} value={`${c.code} ${c.name}`}>{c.name}</option>)}
                    </select>
                  ) : f.key === 'currency' ? (
                    <select value={l.currency || CURRENCIES[0]} onChange={(e) => setLine(i, { currency: e.target.value })}>
                      {CURRENCIES.map((c) => <option key={c}>{c}</option>)}
                    </select>
                  ) : (
                    <input
                      className={f.key === 'amount' ? 'num' : undefined}
                      placeholder={f.key === 'amount' && fx ? '0.00' : ''}
                      value={l[f.key] ?? ''}
                      onChange={(e) => setLine(i, { [f.key]: f.key === 'swift' ? e.target.value.toUpperCase() : e.target.value })}
                    />
                  )}
                </td>
              ))}
              <td className="c">{lines.length > 1 && <button className="btn danger" onClick={() => setLines(lines.filter((_, j) => j !== i))}>삭제</button>}</td>
            </tr>
          ))}
          <tr className="total">
            <td className="l" colSpan={fields.length - 1}>합계 {lines.filter((l) => l.vendor || l.amount).length}건</td>
            <td>{fmtAmt(total, amtDigits(form.form_type))}</td>
            <td />
          </tr>
        </tbody>
      </table>
      {fx && currencies.length > 1 && (
        <div className="hint">통화별 합계 — {currencies.map(([c, v]) => `${c} ${fmtFixed(v, 2)}`).join(' · ')}</div>
      )}
      <div className="row" style={{ marginTop: 10 }}>
        <button className="btn secondary" onClick={() => setLines([...lines, { ...EMPTY_LINE, currency: fx ? CURRENCIES[0] : '' }])}>행 추가</button>
      </div>
      </div>

      </>)}

      <div className="sec-title">상세 내용{proposal && <span style={{ color: 'var(--bad)' }}> *</span>}</div>
      <div className="panel">
        <RichTextEditor
          value={form.note}
          onChange={(html) => setForm((f) => ({ ...f, note: html }))}
          placeholder={proposal
            ? '품의 목적, 배경, 검토 내용, 기대 효과 등 결재에 필요한 내용을 작성하세요.'
            : '지출 목적, 산출 근거, 특이사항 등 결재에 필요한 내용을 작성하세요.'}
        />
        <div className="hint">화면 캡처는 Ctrl+V로 바로 붙여넣을 수 있습니다. 원본 파일이 필요하면 아래 증빙 첨부를 이용하세요.</div>
      </div>

      <div className="sec-title">증빙 첨부</div>
      <div className="panel">
        <AttachmentPicker files={files} onChange={setFiles} />
      </div>

      <div className="panel">
        <div className="row">
          <button className="btn" onClick={submit}
            disabled={busy || oversized || !form.title.trim() || (proposal ? !hasDetail : total <= 0)}>
            {busy ? '상신 중…' : '상신 (기안)'}
          </button>
        </div>
      </div>
    </>
  )
}

function DocList({ box, onToast }) {
  const [list, setList] = useState(null)
  const [openId, setOpenId] = useState(null)
  const [err, setErr] = useState('')
  const load = () => portal.get(`/api/edoc/docs?box=${box}`).then(setList).catch((e) => setErr(e.message))
  useEffect(() => { setOpenId(null); load() }, [box])
  if (err) return <div className="error-box">{err}</div>
  if (!list) return <div className="muted">불러오는 중…</div>
  return (
    <div className="panel">
      <table>
        <thead><tr><th>문서번호</th><th className="l">제목</th><th className="l">양식</th><th className="l">기안자</th><th>금액</th><th>기안일</th><th>상태</th><th /></tr></thead>
        <tbody>
          {list.length === 0 && <tr><td colSpan={8} className="c muted">문서가 없습니다</td></tr>}
          {list.map((d) => (
            <React.Fragment key={d.id}>
              <tr>
                <td className="c">{d.doc_num}</td>
                <td className="l">{d.title}</td>
                <td className="l">{d.form_type}</td>
                <td className="l">{d.drafter_name}</td>
                <td>{fmt(d.total_amount)}</td>
                <td className="c">{String(d.created_at || '').slice(0, 10)}</td>
                <td className="c">
                  <span className={d.display_status?.includes('완료') ? 'ok' : d.display_status === '반려' ? 'warn' : ''}>{d.display_status}</span>
                </td>
                <td className="c"><button className="btn secondary" onClick={() => setOpenId(openId === d.id ? null : d.id)}>{openId === d.id ? '닫기' : '열기'}</button></td>
              </tr>
              {openId === d.id && (
                <tr><td colSpan={8} style={{ background: '#F8FAFC' }}>
                  <DocDetail id={d.id} onActed={(m) => { onToast(m); load() }} />
                </td></tr>
              )}
            </React.Fragment>
          ))}
        </tbody>
      </table>
    </div>
  )
}

// 자금이체 관리 — 계좌이체(원화)와 외화송금은 양식·엑셀이 달라서 탭으로 나눈다
const TRANSFER_TABS = [
  { key: 'krw', label: '계좌이체 (원화)' },
  { key: 'fx', label: '외화송금' },
]

function Transfers({ onToast }) {
  const [tab, setTab] = useState('krw')
  const [rows, setRows] = useState(null)
  const [sel, setSel] = useState(new Set())
  const [onlyUn, setOnlyUn] = useState(true)
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10))
  const [outAcct, setOutAcct] = useState('')
  const [err, setErr] = useState('')
  const fx = tab === 'fx'
  const cols = fx
    ? ['문서번호', '제목', '거래처', '은행', 'SWIFT', '계좌', '예금주', '통화', '금액', '출금계좌', '지급일', '이체']
    : ['문서번호', '제목', '거래처', '은행', '계좌', '예금주', '금액', '출금계좌', '지급일', '이체']

  const load = () => {
    setSel(new Set())
    setRows(null)
    portal.get(`/api/edoc/transfers?group=${tab}&only_untransferred=${onlyUn ? 1 : 0}`)
      .then((r) => setRows(r)).catch((e) => setErr(e.message))
  }
  useEffect(() => { load() }, [onlyUn, tab])

  const act = async (fn) => { try { await fn(); load() } catch (e) { setErr(e.message) } }
  const ids = [...sel]

  if (err) return <div className="error-box">{err}</div>
  return (
    <div className="panel">
      <div className="seg" style={{ marginBottom: 12 }}>
        {TRANSFER_TABS.map((t) => (
          <label key={t.key} className={tab === t.key ? 'on' : ''}>
            <input type="radio" name="edoc-transfer-tab" checked={tab === t.key} onChange={() => setTab(t.key)} />
            {t.label}
          </label>
        ))}
      </div>
      {!rows ? <div className="muted">불러오는 중…</div> : (
      <>
      <div className="row" style={{ marginBottom: 10 }}>
        <label className="row" style={{ gap: 4 }}>
          <input type="checkbox" checked={onlyUn} onChange={(e) => setOnlyUn(e.target.checked)} /> 미이체만
        </label>
        <span className="muted">선택 {sel.size}건:</span>
        <input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
        <button className="btn" disabled={!sel.size}
          onClick={() => act(() => portal.post('/api/edoc/transfers/mark', { line_ids: ids, transfer_date: date }).then(() => onToast('이체완료 처리')))}>이체완료</button>
        <button className="btn secondary" disabled={!sel.size}
          onClick={() => act(() => portal.post('/api/edoc/transfers/unmark', { line_ids: ids }).then(() => onToast('미이체로 되돌림')))}>되돌리기</button>
        <input placeholder="출금계좌" style={{ width: 160 }} value={outAcct} onChange={(e) => setOutAcct(e.target.value)} />
        <button className="btn secondary" disabled={!sel.size || !outAcct.trim()}
          onClick={() => act(() => portal.post('/api/edoc/transfers/out-account', { line_ids: ids, out_account: outAcct }).then(() => onToast('출금계좌 지정')))}>출금계좌 지정</button>
        <a className={`btn secondary ${sel.size ? '' : 'disabled'}`} style={{ textDecoration: 'none' }}
          href={sel.size ? `/api/edoc/transfers/${fx ? 'fx-excel' : 'excel'}?line_ids=${ids.join(',')}` : undefined}>
          {fx ? '외화송금 내역 엑셀' : '대량이체 엑셀'}
        </a>
      </div>
      <table>
        <thead>
          <tr>
            <th style={{ width: 28 }}><input type="checkbox"
              checked={rows.length > 0 && rows.every((r) => sel.has(r.id))}
              onChange={(e) => setSel(e.target.checked ? new Set(rows.map((r) => r.id)) : new Set())} /></th>
            {cols.map((c) => (
              <th key={c} className={c === '금액' || c === '문서번호' || c === '지급일' || c === '이체' || c === '통화' ? '' : 'l'}>{c}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 && <tr><td colSpan={cols.length + 1} className="c muted">대상이 없습니다</td></tr>}
          {rows.map((r) => (
            <tr key={r.id}>
              <td className="c"><input type="checkbox" checked={sel.has(r.id)}
                onChange={() => setSel((p) => { const n = new Set(p); n.has(r.id) ? n.delete(r.id) : n.add(r.id); return n })} /></td>
              <td className="c">{r.doc_num}</td>
              <td className="l">{r.title}</td>
              <td className="l">{r.vendor}</td>
              <td className="l">{r.bank}</td>
              {fx && <td className="l">{r.swift}</td>}
              <td className="l">{r.account}</td>
              <td className="l">{r.holder}</td>
              {fx && <td className="c">{r.currency}</td>}
              <td>{fmtAmt(r.amount, fx ? 2 : 0)}</td>
              <td className="l">{r.out_account}</td>
              <td className="c">{r.pay_date}</td>
              <td className="c">{r.transfer_status === '이체완료' ? <span className="ok">✓ {r.transfer_date}</span> : <span className="muted">미이체</span>}</td>
            </tr>
          ))}
          {rows.length > 0 && (
            <tr className="total">
              <td colSpan={cols.length - 3} className="l">합계 {fmt(rows.length)}건</td>
              <td>{fmtAmt(rows.reduce((s, r) => s + (Number(r.amount) || 0), 0), fx ? 2 : 0)}</td>
              <td colSpan={3} />
            </tr>
          )}
        </tbody>
      </table>
      {fx && rows.length > 0 && (
        <div className="hint">통화별 합계 — {byCurrency(rows).map(([c, v]) => `${c} ${fmtFixed(v, 2)}`).join(' · ')}</div>
      )}
      </>
      )}
    </div>
  )
}

const SUB_LABEL = {
  inbox: '결재함', draft: '기안하기', mine: '내 문서',
  approver: '내가 결재자', transfers: '자금이체 관리', all: '전체문서',
  doc: '문서',
}

// sub는 사이드바(전자결재 대분류의 중분류 메뉴)에서 지정된다
export default function Edoc({ sub = 'inbox', docId = null, currentUserId, autoOpenNoteId, onAutoOpened }) {
  const [me, setMe] = useState(undefined)
  const [done, setDone] = useState(false) // 기안 완료 후 안내
  const [err, setErr] = useState('')
  const [toast, setToast] = useState('')
  const onToast = (m) => { setToast(m); setTimeout(() => setToast(''), 3000) }

  useEffect(() => {
    portal.get('/api/edoc/me').then(setMe).catch((e) => { setMe(null); setErr(e.message) })
  }, [])
  useEffect(() => { setDone(false) }, [sub])

  if (me === undefined) return <div className="muted">불러오는 중…</div>
  if (me === null) return <><h2>전자결재</h2><div className="error-box">{err}</div></>

  return (
    <>
      <div style={{ position: 'relative' }}>
        <h2>
          전자결재 · {SUB_LABEL[sub]}
          <span className="muted" style={{ fontSize: 13 }}> · {me.person?.name} ({me.person?.team} {me.person?.position})</span>
        </h2>
        <NoteDrawer target={`edoc-${sub}`} screenKey={SUB_SCREEN_KEY[sub] || null}
          context={null} contextLabel={null} onNavigate={() => {}}
          autoOpenNoteId={autoOpenNoteId} onAutoOpened={onAutoOpened}
          currentUserId={currentUserId} />
      </div>
      {/* WORK 알림에서 바로 열린 문서 (?doc=123) — 권한 검사는 서버가 한다 */}
      {sub === 'doc' && <div className="panel"><DocDetail id={docId} onActed={(m) => onToast(m)} /></div>}
      {sub === 'inbox' && <Inbox onToast={onToast} />}
      {sub === 'draft' && (done
        ? <div className="panel"><span className="ok">기안이 상신되었습니다.</span> <span className="muted">진행 상황은 [내 문서]에서 확인하세요.</span></div>
        : <Draft onToast={onToast} onDone={() => setDone(true)} />)}
      {sub === 'mine' && <DocList box="mine" onToast={onToast} />}
      {sub === 'approver' && <DocList box="approver" onToast={onToast} />}
      {sub === 'all' && <DocList box="all" onToast={onToast} />}
      {sub === 'transfers' && <Transfers onToast={onToast} />}
      {toast && <div className="toast">{toast}</div>}
    </>
  )
}
